import { createRootRoute, Outlet } from '@tanstack/react-router'
import { QueryClient } from '@tanstack/react-query'
import { storage } from '@/lib/storage'
import { Footer } from '@/components'

export const queryClient = new QueryClient()

// Authentication loader
async function authLoader() {
  const accessToken = storage.get('accessToken')
  const refreshToken = storage.get('refreshToken')
  const userRole = storage.get('userRole')

  return {
    accessToken,
    refreshToken,
    userRole,
  }
}

function NotFound() {
  return (
    <div className="flex flex-col items-center justify-center min-h-screen">
      <h1 className="text-4xl font-bold mb-4">404</h1>
      <p className="text-lg text-muted-foreground mb-4">Page not found</p>
      <a
        href="/"
        className="text-primary hover:underline"
      >
        Go back home
      </a>
    </div>
  )
}

export const Route = createRootRoute({
  beforeLoad: async ({ location }) => {
    // Skip auth check for public routes
    const publicRoutes = ['/auth/login', '/privacy-policy', '/liff/login', '/export']
    if (publicRoutes.includes(location.pathname)) {
      return
    }

    const isLiffRoute = location.pathname.startsWith('/liff')

    const { accessToken, refreshToken, userRole } = await authLoader()

    // LIFF handles auth in LiffBootstrap — do not block before the app mounts
    if (isLiffRoute) {
      if (!accessToken || !refreshToken) {
        return
      }
      const normalizedRole = userRole?.toLowerCase() || ''
      const liffAllowed =
        normalizedRole === 'admin' ||
        normalizedRole.includes('admin') ||
        normalizedRole === 'liff_operator' ||
        normalizedRole === 'super_admin'
      if (!liffAllowed && location.pathname !== '/liff/login') {
        storage.remove('accessToken')
        storage.remove('refreshToken')
        storage.remove('userRole')
        throw new Error('LIFF_FORBIDDEN')
      }
      return
    }

    // If no tokens and trying to access protected route
    if (!accessToken || !refreshToken) {
      if (location.pathname !== '/auth/login') {
        throw new Error('UNAUTHORIZED')
      }
      return
    }

    // If has tokens and trying to access login, allow but will redirect in component
    if (accessToken && refreshToken && location.pathname === '/auth/login') {
      return
    }

    // Verify token with API (only if API URL is configured)
    // Skip API verification for now to allow navigation - can be enabled later
    // const apiUrl = import.meta.env.VITE_API_URL
    // if (apiUrl) {
    //   try {
    //     const controller = new AbortController()
    //     const timeoutId = setTimeout(() => controller.abort(), 5000) // 5 second timeout

    //     const response = await fetch(`${apiUrl}/auth/me`, {
    //       headers: {
    //         Authorization: `Bearer ${accessToken}`,
    //         'Content-Type': 'application/json',
    //       },
    //       method: 'GET',
    //       signal: controller.signal,
    //     })

    //     clearTimeout(timeoutId)

    //     if (!response.ok) {
    //       if (response.status === 401) {
    //         // Clear storage and redirect to login
    //         storage.remove('accessToken')
    //         storage.remove('refreshToken')
    //         storage.remove('userRole')
    //         throw new Error('UNAUTHORIZED')
    //       }
    //       // For other errors, don't throw - just log and continue
    //       console.warn(`API verification failed with status: ${response.status}`)
    //     }
    //   } catch (error) {
    //     if (error instanceof Error && error.message === 'UNAUTHORIZED') {
    //       throw error
    //     }
    //     // On network error or timeout, don't block navigation
    //     // Only throw UNAUTHORIZED if we got a 401 response
    //     if (error instanceof Error && error.name === 'AbortError') {
    //       console.warn('API verification timeout - allowing navigation')
    //     } else {
    //       console.warn('API verification error:', error)
    //     }
    //     // Don't throw on network errors - allow navigation to proceed
    //   }
    // }

    // Check admin access
    if (location.pathname.startsWith('/admin')) {
      // Allow access if userRole is 'admin' or contains 'admin' (case insensitive)
      const normalizedRole = userRole?.toLowerCase() || ''
      if (normalizedRole !== 'admin' && !normalizedRole.includes('admin')) {
        storage.remove('accessToken')
        storage.remove('refreshToken')
        storage.remove('userRole')
        throw new Error('FORBIDDEN')
      }
    }
  },
  errorComponent: ({ error }) => {
    if (error instanceof Error) {
      if (error.message === 'UNAUTHORIZED') {
        window.location.href = '/auth/login'
        return null
      }
      if (error.message === 'LIFF_UNAUTHORIZED') {
        window.location.href = '/liff/login'
        return null
      }
      if (error.message === 'LIFF_FORBIDDEN') {
        window.location.href = '/liff/login'
        return null
      }
      if (error.message === 'FORBIDDEN') {
        window.location.href = '/'
        return null
      }
    }
    return <div>An error occurred: {error instanceof Error ? error.message : 'Unknown error'}</div>
  },
  notFoundComponent: NotFound,
  component: () => {
    console.log('🟡 Root component rendered')
    return (
      <>
        <Outlet />
      </>
    )
  },
})

